import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Select,
  Heading,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
} from '@chakra-ui/react';
import { usePagination, useSortBy, useTable } from 'react-table';
import { MdFileDownload } from 'react-icons/md';
import Card from 'components/card/Card';
import DateRangePicker from 'components/datePicker/DateRangePicker';
import { downloadNewUsers } from '../../../../functions/generatePayrollFile';
import { prettifyDate } from '../../../../functions/dateUtils';
import { axiosInstance } from '../../../../api/httpClient';
import { API } from '../../../../api/env';
import CompanyDropdownSelection from '../../../../functions/CompanyDropdownSelection';

const NewUsersModal = ({ isOpen, onClose }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isCacheLoaded] = useState(true);
  const [dropdownResetKey, setDropdownResetKey] = useState(0);
  const [filters, setFilters] = useState({
    companyIds: [],
    startDate: '',
    endDate: '',
    page: 1,
    resultsPerPage: 10,
  });

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const columns = useMemo(
    () => [
      {
        Header: 'שם החברה',
        accessor: 'companyName',
        width: '200px',
      },
      {
        Header: 'שם מלא',
        accessor: 'fullName',
        width: '200px',
      },
      {
        Header: 'תאריך רישום',
        accessor: 'createdDate',
        width: '150px',
        Cell: ({ value }) => prettifyDate(value),
      },
      {
        Header: 'מספר טלפון',
        accessor: 'phoneNumber',
        width: '150px',
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize },
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 10 },
      pageCount,
      manualPagination: true,
      autoResetPage: false,
    },
    useSortBy,
    usePagination
  );


  const getNewUsersReport = async (queryFilters) => {
    const userInfo = JSON.parse(localStorage.getItem("user"));
    const accessToken = userInfo.token;
    const config = {
      method: 'post',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + accessToken
      }
    };

    const requestBody = {
      reportName: 'new-users',
      filters: queryFilters
    };

    return axiosInstance.post(`${API}/admin/reports`, requestBody, config);
  };

  const fetchData = async (overrideFilters) => {
    setLoading(true);
    try {
      const queryFilters = {
        ...(overrideFilters ?? filters),
        page: pageIndex + 1,
        resultsPerPage: pageSize,
      };

      // Remove empty filters
      Object.keys(queryFilters).forEach(key => {
        const v = queryFilters[key];
        if (v === '' || v === null || v === undefined || (Array.isArray(v) && v.length === 0)) {
          delete queryFilters[key];
        }
      });

      const response = await getNewUsersReport(queryFilters);
      if (response.data) {
        const users = response.data.users || [];
        setData(users);
        setPageCount(response.data.totalPages || 0);
        setTotalCount(response.data.totalCount || 0);
      }
    } catch (error) {
      console.error('Error fetching new users:', error);
      setData([]);
      setPageCount(0);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isCacheLoaded) {
      fetchData();
    }
  }, [isCacheLoaded, pageIndex, pageSize]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleDateRangeChange = (dateRange) => {
    setFilters(prev => ({
      ...prev,
      startDate: dateRange?.from ? dateRange.from.toISOString().split('T')[0] : '',
      endDate: dateRange?.to ? dateRange.to.toISOString().split('T')[0] : '',
    }));
  };

  const applyFilters = () => {
    gotoPage(0);
    fetchData(filters);
  };

  const emptyFilters = { companyIds: [], startDate: '', endDate: '', page: 1, resultsPerPage: 10 };

  const resetFilters = () => {
    setFilters(emptyFilters);
    setDropdownResetKey((k) => k + 1);
    gotoPage(0);
    fetchData(emptyFilters);
  };

  const handleExport = async () => {
    try {
      setLoading(true);

      // Prepare filters for export (all data, no pagination)
      const exportFilters = {
        ...filters,
        exportAll: true,
      };

      // Remove empty filters
      Object.keys(exportFilters).forEach(key => {
        const v = exportFilters[key];
        if (v === '' || v === null || v === undefined || (Array.isArray(v) && v.length === 0)) {
          delete exportFilters[key];
        }
      });

      // Fetch all data matching the current filters
      const response = await getNewUsersReport(exportFilters);

      if (response.data && response.data.users) {
        downloadNewUsers(response.data.users);
      }
    } catch (error) {
      console.error('Error exporting new users:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl">
      <ModalOverlay />
      <ModalContent maxW="90vw" maxH="90vh">
        <ModalHeader>
          <Flex justify="space-between" align="center">
            <Heading size="lg" color={textColor}>
              משתמשים חדשים
            </Heading>
            <Button
              leftIcon={<Icon as={MdFileDownload} />}
              colorScheme="blue"
              onClick={handleExport}
              isLoading={loading}
              isDisabled={totalCount === 0}
              mr="40px"
            >
              ייצא לאקסל ({totalCount} רשומות)
            </Button>
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody overflowY="auto">
          <Card>
            <Flex direction="column" w="100%">
              {/* Filters */}
              <Flex gap={4} mb={6} wrap="wrap" align="end">
            <Box>
              <Text mb={2} fontSize="sm" fontWeight="medium">
                חברה
              </Text>
              <CompanyDropdownSelection
                key={`${isOpen}-${dropdownResetKey}`}
                showAll={false}
                selectedCompany={(companies) =>
                  handleFilterChange('companyIds', companies.map((c) => c.id))
                }
                style={{ minWidth: 200 }}
              />
            </Box>

            <Box>
              <Text mb={2} fontSize="sm" fontWeight="medium">
                טווח תאריכים
              </Text>
              <DateRangePicker
                value={{
                  from: filters.startDate ? new Date(filters.startDate) : undefined,
                  to: filters.endDate ? new Date(filters.endDate) : undefined,
                }}
                onChange={handleDateRangeChange}
                width="200px"
              />
            </Box>

            <Button colorScheme="blue" onClick={applyFilters} isLoading={loading}>
              חפש
            </Button>
            <Button variant="outline" onClick={resetFilters}>
              נקה
            </Button>
          </Flex>

          {/* Results count */}
          <Text mb={4} fontSize="sm" color="gray.600">
            נמצאו {totalCount} תוצאות
          </Text>

          {/* Table */}
          <Table {...getTableProps()} variant="simple" color="gray.500" mb="24px">
            <Thead>
              {headerGroups.map((headerGroup, index) => (
                <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                  {headerGroup.headers.map((column, index) => (
                    <Th
                      {...column.getHeaderProps(column.getSortByToggleProps())}
                      pe="10px"
                      key={index}
                      borderColor={borderColor}
                    >
                      <Flex
                        justify="space-between"
                        align="center"
                        fontSize={{ sm: '10px', lg: '12px' }}
                        color="gray.400"
                      >
                        {column.render('Header')}
                      </Flex>
                    </Th>
                  ))}
                </Tr>
              ))}
            </Thead>
            <Tbody {...getTableBodyProps()}>
              {page.map((row, index) => {
                prepareRow(row);
                return (
                  <Tr {...row.getRowProps()} key={index}>
                    {row.cells.map((cell, index) => (
                      <Td
                        {...cell.getCellProps()}
                        key={index}
                        fontSize={{ sm: '14px' }}
                        minW={{ sm: '150px', md: '200px', lg: 'auto' }}
                        borderColor="transparent"
                      >
                        <Text color={textColor} fontSize="sm" fontWeight="700">
                          {cell.render('Cell')}
                        </Text>
                      </Td>
                    ))}
                  </Tr>
                );
              })}
            </Tbody>
          </Table>

          {/* Pagination */}
          <Flex justify="space-between" align="center" mt={4}>
            <Flex align="center" gap={2}>
              <Text fontSize="sm">הצג</Text>
              <Select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                width="80px"
                size="sm"
              >
                {[10, 20, 30, 40, 50].map((size) => (
                  <option key={size} value={size}>
                    {size}
                  </option>
                ))}
              </Select>
              <Text fontSize="sm">תוצאות</Text>
            </Flex>

            <Flex align="center" gap={2}>
              <Button
                size="sm"
                onClick={() => gotoPage(0)}
                isDisabled={!canPreviousPage}
              >
                {'<<'}
              </Button>
              <Button
                size="sm"
                onClick={() => previousPage()}
                isDisabled={!canPreviousPage}
              >
                {'<'}
              </Button>
              <Button
                size="sm"
                onClick={() => nextPage()}
                isDisabled={!canNextPage}
              >
                {'>'}
              </Button>
              <Button
                size="sm"
                onClick={() => gotoPage(pageCount - 1)}
                isDisabled={!canNextPage}
              >
                {'>>'}
              </Button>
            </Flex>

            <Flex align="center" gap={2}>
              <Text fontSize="sm">עמוד</Text>
              <NumberInput
                value={pageIndex + 1}
                onChange={(_, value) => {
                  const page = value ? value - 1 : 0;
                  gotoPage(page);
                }}
                size="sm"
                width="80px"
                min={1}
                max={pageOptions.length}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <Text fontSize="sm">מתוך {pageOptions.length}</Text>
            </Flex>
          </Flex>
        </Flex>
      </Card>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default NewUsersModal;
